import { useEffect, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"

import EmissaoService from "src/services/emissao-service"

const initialState = { data: null, rows: [], loading: false, error: null }

const sortByDate = (rows, field = "DataReferencia") => [...rows].sort((first, second) => {
    const firstDate = new Date(first?.[field] ?? 0)
    const secondDate = new Date(second?.[field] ?? 0)
    return secondDate - firstDate
})

const toSummaryState = (rows) => ({
    data: rows[0] ?? null,
    rows,
    loading: false,
    error: null,
})

const getRows = (response) => response?.body?.results ?? []

export const useCompanySummaryData = ({ company, refreshKey = 0 }) => {
    const datalakeServiceRef = useRef(new DatalakeService())
    const emissaoServiceRef = useRef(new EmissaoService())
    const identifiersRef = useRef(null)
    const [state, setState] = useState({
        rating: initialState,
        event: initialState,
        fact: initialState,
        probabilityDefault: initialState,
    })

    useEffect(() => {
        const cpfCnpj = company?.CPFCNPJ
        const codigoCvm = company?.CodigoCVM
        const raizCnpj = company?.RaizCNPJ
        let active = true

        if (!cpfCnpj) {
            setState({
                rating: initialState,
                event: initialState,
                fact: initialState,
                probabilityDefault: initialState,
            })
            return undefined
        }

        const identifiers = {
            cpfCnpj,
            codigoCvm,
            raizCnpj,
        }

        const isSameCompany =
            identifiersRef.current?.cpfCnpj === cpfCnpj &&
            identifiersRef.current?.codigoCvm === codigoCvm &&
            identifiersRef.current?.raizCnpj === raizCnpj

        identifiersRef.current = identifiers

        setState((current) => ({
            rating: {
                ...(isSameCompany ? current.rating : initialState),
                loading: true,
                error: null,
            },
            event: {
                ...(isSameCompany ? current.event : initialState),
                loading: true,
                error: null,
            },
            fact: codigoCvm
                ? {
                    ...(isSameCompany ? current.fact : initialState),
                    loading: true,
                    error: null,
                }
                : initialState,
            probabilityDefault: raizCnpj
                ? {
                    ...(isSameCompany
                        ? current.probabilityDefault
                        : initialState),
                    loading: true,
                    error: null,
                }
                : initialState,
        }))

        const load = async () => {
            const [rating, event, fact, probabilityDefault] = await Promise.allSettled([
                emissaoServiceRef.current.ratingDevedor(
                    "get",
                    `?CPFCNPJ=${encodeURIComponent(cpfCnpj)}`
                ),
                emissaoServiceRef.current.eventoCreditoDevedor(
                    "get",
                    `?CPFCNPJ=${encodeURIComponent(cpfCnpj)}`
                ),

                codigoCvm
                    ? datalakeServiceRef.current.loadFatoRelevante(codigoCvm)
                    : Promise.resolve(null),
                raizCnpj
                    ? datalakeServiceRef.current.loadPD(raizCnpj)
                    : Promise.resolve(null),
            ])

            if (!active) return

            const ratingRows = rating.status === "fulfilled"
                ? sortByDate(getRows(rating.value))
                : []
                
            const eventRows = event.status === "fulfilled"
                ? sortByDate(getRows(event.value))
                : []

            const factRows = fact.status === "fulfilled"
                ? sortByDate(getRows(fact.value))
                : []

            const probabilityDefaultRows = probabilityDefault.status === "fulfilled"
                ? sortByDate(getRows(probabilityDefault.value))
                : []

            setState({
                rating:
                    rating.status === "fulfilled"
                        ? toSummaryState(ratingRows)
                        : { ...initialState, error: "Não foi possível carregar o rating." },
                event:
                    event.status === "fulfilled"
                        ? toSummaryState(eventRows)
                        : { ...initialState, error: "Não foi possível carregar o evento de crédito." },
                fact: !codigoCvm
                    ? initialState
                    : fact.status === "fulfilled"
                        ? toSummaryState(factRows)
                        : { ...initialState, error: "Não foi possível carregar o fato relevante." },
                probabilityDefault: !raizCnpj
                    ? initialState
                    : probabilityDefault.status === "fulfilled"
                        ? toSummaryState(probabilityDefaultRows)
                        : { ...initialState, error: "Não foi possível carregar a probabilidade de default." },
            })
        }

        void load()

        return () => {
            active = false
        }
    }, [company?.CPFCNPJ, company?.CodigoCVM, company?.RaizCNPJ, refreshKey])

    return state
}